import React, { useState, useEffect } from 'react';
import { getBackendURL } from '../../utils/backendURL';
import axios from 'axios';
import { 
  TrendingUp, 
  Users, 
  Activity, 
  Calendar,
  BarChart3,
  PieChart,
  RefreshCw,
  Download,
  AlertCircle,
  Filter,
  ChevronDown
} from 'lucide-react';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState(30);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  const timeRanges = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 365, label: 'Last year' }
  ];

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${getBackendURL()}/api/ops/analytics/usage?days=${timeRange}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const calculateTrend = (data) => {
    if (!data || data.length < 2) return { value: 0, isPositive: true };
    
    const recent = data.slice(-7).reduce((sum, item) => sum + item.total_tests, 0);
    const previous = data.slice(-14, -7).reduce((sum, item) => sum + item.total_tests, 0);
    
    if (previous === 0) return { value: 0, isPositive: true };
    
    const trend = ((recent - previous) / previous) * 100;
    return { value: Math.abs(trend).toFixed(1), isPositive: trend >= 0 };
  };

  const MetricCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <div className="admin-card p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      {trend && (
        <div className="mt-4 flex items-center">
          <TrendingUp className={`h-4 w-4 mr-1 ${trend.isPositive ? 'text-green-500' : 'text-red-500 transform rotate-180'}`} />
          <span className={`text-sm font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {trend.isPositive ? '+' : '-'}{trend.value}% from last period
          </span>
        </div>
      )}
    </div>
  );

  const SimpleChart = ({ data, title, type = 'line' }) => {
    if (!data || data.length === 0) {
      return (
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="flex items-center justify-center h-64 text-gray-500">
            <div className="text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No data available</p>
            </div>
          </div>
        </div>
      );
    }

    // Filter to show only days with data for better visualization
    const dataWithActivity = data.filter(item => item.total_tests > 0 || item.unique_users > 0);
    const displayData = dataWithActivity.length > 0 ? dataWithActivity : data.slice(-7);
    
    const maxValue = Math.max(...displayData.map(item => item.total_tests), 1);
    const maxUsers = Math.max(...displayData.map(item => item.unique_users), 1);
    
    // SVG dimensions
    const width = 900;
    const height = 300;
    const padding = { top: 20, right: 40, bottom: 60, left: 60 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    // Calculate positions for line chart
    const getX = (index) => {
      return padding.left + (index / Math.max(displayData.length - 1, 1)) * chartWidth;
    };
    
    const getYTests = (value) => {
      return padding.top + chartHeight - (value / maxValue) * chartHeight;
    };
    
    const getYUsers = (value) => {
      return padding.top + chartHeight - (value / maxUsers) * chartHeight;
    };
    
    // Create path strings for lines
    const testsPath = displayData.map((item, index) => {
      const x = getX(index);
      const y = getYTests(item.total_tests);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
    
    const usersPath = displayData.map((item, index) => {
      const x = getX(index);
      const y = getYUsers(item.unique_users);
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');

    return (
      <div className="admin-card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">{title}</h3>
        
        {/* Line chart visualization */}
        <div className="space-y-4">
          {/* Legend */}
          <div className="flex items-center justify-center space-x-8 text-sm mb-4 bg-gray-50 py-3 rounded-lg">
            <div className="flex items-center">
              <div className="w-8 h-0.5 bg-blue-500 mr-2"></div>
              <span className="text-blue-700 font-medium">Total Tests (Left Axis)</span>
            </div>
            <div className="flex items-center">
              <div className="w-8 h-0.5 bg-green-500 mr-2"></div>
              <span className="text-green-700 font-medium">Unique Users (Right Axis)</span>
            </div>
          </div>
          
          {/* SVG Line Chart */}
          <div className="w-full overflow-x-auto">
            <svg 
              viewBox={`0 0 ${width} ${height}`} 
              className="w-full h-auto"
              style={{ minHeight: '300px', maxHeight: '400px' }}
            >
              {/* Grid lines */}
              {[0, 1, 2, 3, 4].map((i) => {
                const y = padding.top + (chartHeight / 4) * i;
                return (
                  <g key={i}>
                    <line
                      x1={padding.left}
                      y1={y}
                      x2={width - padding.right}
                      y2={y}
                      stroke="#E5E7EB"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                    />
                    {/* Left Y-axis labels (Tests) */}
                    <text
                      x={padding.left - 10}
                      y={y + 4}
                      textAnchor="end"
                      className="text-xs fill-blue-600 font-medium"
                      fontSize="11"
                    >
                      {Math.round(maxValue * (4 - i) / 4)}
                    </text>
                    {/* Right Y-axis labels (Users) */}
                    <text
                      x={width - padding.right + 10}
                      y={y + 4}
                      textAnchor="start"
                      className="text-xs fill-green-600 font-medium"
                      fontSize="11"
                    >
                      {Math.round(maxUsers * (4 - i) / 4)}
                    </text>
                  </g>
                );
              })}
              
              {/* X-axis */}
              <line
                x1={padding.left}
                y1={height - padding.bottom}
                x2={width - padding.right}
                y2={height - padding.bottom}
                stroke="#9CA3AF"
                strokeWidth="2"
              />
              
              {/* Left Y-axis (Tests) */}
              <line
                x1={padding.left}
                y1={padding.top}
                x2={padding.left}
                y2={height - padding.bottom}
                stroke="#3B82F6"
                strokeWidth="2"
              />
              
              {/* Right Y-axis (Users) */}
              <line
                x1={width - padding.right}
                y1={padding.top}
                x2={width - padding.right}
                y2={height - padding.bottom}
                stroke="#10B981"
                strokeWidth="2"
              />
              
              {/* Tests line */}
              <path
                d={testsPath}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* Users line */}
              <path
                d={usersPath}
                fill="none"
                stroke="#10B981"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* Data points for Tests */}
              {displayData.map((item, index) => {
                const x = getX(index);
                const y = getYTests(item.total_tests);
                return (
                  <g key={`test-${index}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r="5"
                      fill="#3B82F6"
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-pointer hover:r-7"
                    >
                      <title>{`${item.total_tests} tests on ${new Date(item.date).toLocaleDateString()}`}</title>
                    </circle>
                    {item.total_tests > 0 && (
                      <text
                        x={x}
                        y={y - 12}
                        textAnchor="middle"
                        className="text-xs font-bold fill-blue-600"
                        fontSize="12"
                      >
                        {item.total_tests}
                      </text>
                    )}
                  </g>
                );
              })}
              
              {/* Data points for Users */}
              {displayData.map((item, index) => {
                const x = getX(index);
                const y = getYUsers(item.unique_users);
                return (
                  <g key={`user-${index}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r="4"
                      fill="#10B981"
                      stroke="white"
                      strokeWidth="2"
                      className="cursor-pointer"
                    >
                      <title>{`${item.unique_users} users on ${new Date(item.date).toLocaleDateString()}`}</title>
                    </circle>
                  </g>
                );
              })}
              
              {/* X-axis labels */}
              {displayData.map((item, index) => {
                const x = getX(index);
                return (
                  <text
                    key={`label-${index}`}
                    x={x}
                    y={height - padding.bottom + 20}
                    textAnchor="middle"
                    className="text-xs fill-gray-600"
                    fontSize="11"
                  >
                    {new Date(item.date).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </text>
                );
              })}
              
              {/* Axis labels */}
              <text
                x={padding.left - 45}
                y={height / 2}
                textAnchor="middle"
                className="text-sm fill-blue-600 font-semibold"
                fontSize="12"
                transform={`rotate(-90, ${padding.left - 45}, ${height / 2})`}
              >
                Tests
              </text>
              <text
                x={width - padding.right + 45}
                y={height / 2}
                textAnchor="middle"
                className="text-sm fill-green-600 font-semibold"
                fontSize="12"
                transform={`rotate(90, ${width - padding.right + 45}, ${height / 2})`}
              >
                Users
              </text>
            </svg>
          </div>
          
          {/* Show info if data is filtered */}
          {dataWithActivity.length < data.length && dataWithActivity.length > 0 && (
            <div className="mt-4 text-sm text-blue-600 text-center bg-blue-50 rounded-lg py-2">
              📊 Showing {dataWithActivity.length} active days out of {data.length} days total
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">
              {displayData.reduce((sum, item) => sum + item.total_tests, 0)}
            </p>
            <p className="text-sm text-gray-600">Total Tests</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {Math.max(...displayData.map(item => item.unique_users))}
            </p>
            <p className="text-sm text-gray-600">Peak Daily Users</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-purple-600">
              {dataWithActivity.length}
            </p>
            <p className="text-sm text-gray-600">Active Days</p>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-10 bg-gray-200 rounded w-32 animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="admin-card p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="admin-card p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6 border-red-200 bg-red-50">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <p className="text-red-800">{error}</p>
        </div>
        <button
          onClick={fetchAnalytics}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const analyticsData = analytics?.analytics || [];
  const trend = calculateTrend(analyticsData);
  const totalTests = analyticsData.reduce((sum, item) => sum + item.total_tests, 0);
  
  // Get unique users across all days (not summed per day)
  const uniqueUsersSet = new Set();
  analyticsData.forEach(item => {
    // This is still approximate since we don't have user IDs in the response
    // But we'll use the max daily users as a better estimate
  });
  const totalUsers = analytics?.summary?.max_daily_users || 0;
  
  // Calculate averages based on days with actual data, not total days
  const daysWithData = analyticsData.filter(item => item.total_tests > 0).length;
  const avgTestsPerDay = daysWithData > 0 ? (totalTests / daysWithData).toFixed(1) : 0;
  const avgUsersPerDay = daysWithData > 0 ? (totalUsers / daysWithData).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Platform usage metrics and insights</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Time Range Selector */}
          <div className="relative">
            <button
              onClick={() => setShowTimeDropdown(!showTimeDropdown)}
              className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {timeRanges.find(range => range.value === timeRange)?.label}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            
            {showTimeDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                {timeRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => {
                      setTimeRange(range.value);
                      setShowTimeDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${
                      timeRange === range.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                    } first:rounded-t-lg last:rounded-b-lg`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button 
            onClick={fetchAnalytics}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
          
          <button className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Tests Generated"
          value={totalTests.toLocaleString()}
          icon={Activity}
          color="bg-gradient-to-r from-blue-500 to-blue-600"
          subtitle={`${analytics?.period} period`}
          trend={trend}
        />
        
        <MetricCard
          title="Daily Average Tests"
          value={avgTestsPerDay}
          icon={BarChart3}
          color="bg-gradient-to-r from-green-500 to-green-600"
          subtitle="Per day average"
        />
        
        <MetricCard
          title="Total Active Users"
          value={totalUsers.toLocaleString()}
          icon={Users}
          color="bg-gradient-to-r from-purple-500 to-purple-600"
          subtitle="Unique users"
        />
        
        <MetricCard
          title="Daily Average Users"
          value={avgUsersPerDay}
          icon={TrendingUp}
          color="bg-gradient-to-r from-orange-500 to-orange-600"
          subtitle="Per day average"
        />
      </div>

      {/* Usage Chart */}
      <SimpleChart 
        data={analyticsData} 
        title="Usage Trends Over Time"
      />

      {/* Additional Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Peak Usage Days */}
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Usage Days</h3>
          <div className="space-y-3">
            {analyticsData
              .sort((a, b) => b.total_tests - a.total_tests)
              .slice(0, 5)
              .map((day, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(day.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="text-xs text-gray-600">{day.unique_users} unique users</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600">{day.total_tests}</p>
                  <p className="text-xs text-gray-500">tests</p>
                </div>
              </div>
            ))}
            
            {analyticsData.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No usage data available</p>
              </div>
            )}
          </div>
        </div>

        {/* Usage Distribution */}
        <div className="admin-card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Distribution</h3>
          <div className="space-y-4">
            {/* Weekday vs Weekend */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Weekdays</span>
                <span className="text-sm font-medium text-gray-900">
                  {analyticsData.filter(day => {
                    const dayOfWeek = new Date(day.date).getDay();
                    return dayOfWeek >= 1 && dayOfWeek <= 5;
                  }).reduce((sum, day) => sum + day.total_tests, 0)} tests
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full" 
                  style={{ 
                    width: `${totalTests > 0 ? 
                      (analyticsData.filter(day => {
                        const dayOfWeek = new Date(day.date).getDay();
                        return dayOfWeek >= 1 && dayOfWeek <= 5;
                      }).reduce((sum, day) => sum + day.total_tests, 0) / totalTests) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">Weekends</span>
                <span className="text-sm font-medium text-gray-900">
                  {analyticsData.filter(day => {
                    const dayOfWeek = new Date(day.date).getDay();
                    return dayOfWeek === 0 || dayOfWeek === 6;
                  }).reduce((sum, day) => sum + day.total_tests, 0)} tests
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ 
                    width: `${totalTests > 0 ? 
                      (analyticsData.filter(day => {
                        const dayOfWeek = new Date(day.date).getDay();
                        return dayOfWeek === 0 || dayOfWeek === 6;
                      }).reduce((sum, day) => sum + day.total_tests, 0) / totalTests) * 100 : 0}%`
                  }}
                ></div>
              </div>
            </div>

            {/* Average tests per user */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {totalUsers > 0 ? (totalTests / (totalUsers / analyticsData.length || 1)).toFixed(1) : 0}
                </p>
                <p className="text-sm text-gray-600">Average tests per user</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Summary */}
      <div className="admin-card p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Analytics Summary</h3>
            <p className="text-sm text-gray-600">
              Data collected over {analytics?.period} showing platform usage patterns
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">
              Last updated: {new Date().toLocaleString()}
            </p>
            <p className="text-xs text-gray-400">
              Data refreshes automatically every hour
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;